/**
 * Login.js - Login Page
 * 
 * Handles user authentication with email and password.
 * Redirects to appropriate dashboard based on user role.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

// Capture install prompt globally before React mounts
let deferredInstallPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredInstallPrompt = e;
});

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showInstallHint, setShowInstallHint] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const { login } = useAuth();
  const { theme, colors, darkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');

  // Check if we should show install hint
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    setIsIOS(isIOSDevice);
    setShowInstallHint(isMobile && !isStandalone);

    // Check if we captured the install prompt before mount
    if (deferredInstallPrompt) {
      setInstallPrompt(deferredInstallPrompt);
    }

    // Listen for install prompt if it fires later
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      deferredInstallPrompt = e;
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  // Handle Android install button click
  const handleInstallClick = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallHint(false);
    }
    setInstallPrompt(null);
    deferredInstallPrompt = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Login is now async to support IP address logging
    const result = await login(username, password);
    if (result.success) {
      // Check for redirect parameter first, otherwise redirect based on role
      if (redirectPath) {
        navigate(redirectPath);
      } else if (result.user.role === 'ADMIN' || result.user.role === 'SUPER_ADMIN') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
  };


  // Styles
  // Select background image based on theme
  const bgImage = darkMode 
    ? '/assets/backgrounds/login-bg-dark.jpg' 
    : '/assets/backgrounds/login-bg.jpg';

  // Select logo based on theme
  const logoImage = darkMode
    ? '/assets/logos/login-main-dark.png'
    : '/assets/logos/login-main.png';

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    // Background image with overlay for readability
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${bgImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed'
  };

  const formStyle = {
    backgroundColor: theme.cardBackground,
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    width: '100%',
    maxWidth: '400px',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)'
  };

  const titleStyle = {
    textAlign: 'center',
    color: theme.text,
    marginBottom: '10px',
    fontSize: '1.8rem'
  };

  const logoStyle = {
    display: 'block',
    margin: '0 auto 15px auto',
    maxWidth: '250px',
    height: 'auto'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    marginBottom: '15px',
    border: `1px solid ${theme.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    backgroundColor: theme.background,
    color: theme.text,
    boxSizing: 'border-box'
  };


  const buttonStyle = {
    width: '100%',
    padding: '12px',
    backgroundColor: colors.green,
    color: darkMode ? '#1a1a2e' : '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '10px'
  };

  const errorStyle = {
    backgroundColor: colors.red,
    color: darkMode ? '#fff' : '#000',
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '15px',
    textAlign: 'center'
  };

  const hintStyle = {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: theme.backgroundSecondary,
    borderRadius: '4px',
    fontSize: '0.85rem',
    color: theme.textSecondary
  };

  const installHintStyle = {
    marginTop: '15px',
    padding: '15px',
    backgroundColor: '#fff3e0',
    borderRadius: '4px',
    fontSize: '0.85rem',
    color: '#e65100',
    border: '1px solid #ffcc80',
    textAlign: 'center'
  };

  return (
    <div style={containerStyle}>
      <form style={formStyle} onSubmit={handleSubmit}>
        {/* Logo and title */}
        <img 
          src={logoImage} 
          alt="Creative Play Learning Center" 
          style={logoStyle}
        />
        <h1 style={titleStyle}>Employee Portal</h1>
        
        {error && <div style={errorStyle}>{error}</div>}
        
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          style={inputStyle}
          required
        />
        
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle}
          required
        />
        
        <button type="submit" style={buttonStyle}>
          Login
        </button>
        
        <div style={hintStyle}>
          Need an account? Contact your administrator.
        </div>

        {showInstallHint && (
          <div style={installHintStyle}>
            <strong>📲 Install this app!</strong>
            <br />
            {isIOS ? (
              <>Tap <strong>Share</strong> → <strong>Add to Home Screen</strong></>
            ) : installPrompt ? (
              <button
                onClick={handleInstallClick}
                style={{
                  marginTop: '10px',
                  padding: '10px 24px',
                  backgroundColor: '#e65100',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Install Now
              </button>
            ) : (
              <>Tap <strong>⋮ Menu</strong> → <strong>Install app</strong></>
            )}
          </div>
        )}
      </form>
    </div>
  );
}

export default Login;
